import React, {useState, useEffect} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  ChoiceList,
  Stack,
  Button,
  TextStyle,
  Icon,
  ButtonGroup,
} from '@shopify/polaris';
import {ArrowLeftIcon} from '@shopify/polaris-icons';
import MainLayout from '../../layouts/MainLayout';
import AvadaTinyMCEEditor from '../../components/TinyMCE/AvadaTinyMCEEditor';
import {APP_OPTIONS, CHOICE_OPTIONS, TYPE_OPTIONS} from '../../const/notification';
import {addNotification, updateNotification, getNotificationById} from './service/notification';
import {toast} from '../../utils/toast';
import {useNotification} from './context/NotificationContext';

export default function UpsertNotification() {
  const history = useHistory();
  const {action} = useParams();
  const isEditing = action !== 'create';
  const {selectedNotification, setSelectedNotification} = useNotification();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    excerpt: '',
    type: 'feature',
    app: 'seo',
    status: ['draft'],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadNotification = async () => {
      if (!isEditing) return;

      if (selectedNotification) {
        setFormData({
          title: selectedNotification.title || '',
          description: selectedNotification.description || '',
          excerpt: selectedNotification.excerpt || '',
          type: selectedNotification.type || 'feature',
          app: selectedNotification.app || 'seo',
          status: selectedNotification.status ? [selectedNotification.status] : ['published'],
        });
        return;
      }

      if (action && action !== 'create') {
        setLoading(true);
        const resp = await getNotificationById(action);
        if (resp?.success && resp.data) {
          const notification = resp.data;
          setSelectedNotification(notification);
          setFormData({
            title: notification.title || '',
            description: notification.description || '',
            excerpt: notification.excerpt || '',
            type: notification.type || 'feature',
            app: notification.app || 'seo',
            status: notification.status ? [notification.status] : ['published'],
          });
        }
        setLoading(false);
      }
    };

    loadNotification();
  }, [isEditing, selectedNotification, action, setSelectedNotification]);

  useEffect(() => {
    return () => {
      setSelectedNotification(null);
    };
  }, [setSelectedNotification]);

  const handleInputChange = (field) => (value) => {
    setFormData((prev) => ({...prev, [field]: value}));
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: null}));
    }
  };

  const handleBack = () => {
    history.push('/notification');
  };

  const handleDiscard = () => {
    history.push('/notification');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.app) {
      newErrors.app = 'Please select an app';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let resp;
      if (isEditing && selectedNotification?.id) {
        resp = await updateNotification(selectedNotification.id, formData);
        if (resp?.success) {
          toast.success('Notification updated successfully');
          history.push('/notification');
        }
      } else {
        resp = await addNotification(formData);
        if (resp?.success) {
          toast.success('Notification added successfully');
          history.push('/notification');
        }
      }
      if (resp?.error) {
        toast.error(resp.error);
      }
    } catch (error) {
      console.error('Error saving notification:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Avada apps manager">
      <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack alignment="center" spacing="tight">
            <Button plain icon={<Icon source={ArrowLeftIcon} />} onClick={handleBack} />
            <TextStyle variation="strong">
              {isEditing ? 'Edit Notification' : 'Create Notification'}
            </TextStyle>
          </Stack>
          <ButtonGroup>
            <Button onClick={handleDiscard}>Discard</Button>
            <Button primary onClick={handleSave} loading={loading}>
              Save
            </Button>
          </ButtonGroup>
        </div>

        <div style={{display: 'flex', gap: '16px'}}>
          <div style={{width: '360px', flexShrink: 0}}>
            <Card sectioned>
              <FormLayout>
                <ChoiceList
                  title="Status"
                  choices={CHOICE_OPTIONS}
                  selected={formData.status}
                  onChange={handleInputChange('status')}
                />

                <Select
                  label="App"
                  options={APP_OPTIONS}
                  value={formData.app}
                  onChange={handleInputChange('app')}
                  error={errors.app}
                />

                <Select
                  label="Type"
                  options={TYPE_OPTIONS}
                  value={formData.type}
                  onChange={handleInputChange('type')}
                />

                <TextField
                  label="Excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange('excerpt')}
                  multiline={3}
                  placeholder="Short description for preview"
                />
              </FormLayout>
            </Card>
          </div>

          <div style={{flex: 1}}>
            <Card sectioned>
              <FormLayout>
                <TextField
                  label="Title"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  error={errors.title}
                  placeholder="Enter notification title"
                />

                <div>
                  <TextStyle>Description</TextStyle>
                  <div style={{marginTop: '4px'}}>
                    <AvadaTinyMCEEditor
                      id="notification-description"
                      value={formData.description}
                      setValue={handleInputChange('description')}
                      initHeight={600}
                    />
                  </div>
                  {errors.description && (
                    <div style={{color: '#d72c0d', marginTop: '4px'}}>
                      <TextStyle variation="negative">{errors.description}</TextStyle>
                    </div>
                  )}
                </div>
              </FormLayout>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
